/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Inject, Injectable} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import * as _ from 'lodash';
import {AppEventBusService} from '../../app/common/events/app-event-bus.service';

import {AUTH_EVENT} from '../auth/auth-constants';
import {AuthenticatedUserService} from '../../app/common/controller/authenticated-user.service';
import {ElectionApiService} from '../../app/common/controller/election-api.service';
import {NetworkInfoApiService} from '../../app/base/simple/network-info-api.service';
import {LocalNetworkInfo} from '../session/local-network-info';
import {LocalNetworkProfile} from '../session/local-network-profile';
import {LocalPausing} from '../session/local-pausing';
import {LocalDebugInfo} from '../session/local-debug-info';
import {LocalUserPermissions} from '../session/local-user-permissions';
import {LocalUserProfile} from '../session/local-user-profile';
import {ApiConfig} from '../../app/config/api-config';
import {UpdateSession} from '../session/update-session';
import {WorkspaceRegistryService} from '../workspace-registry.service';
import {WINDOW} from '../../app/angular-platform-providers';

@Injectable({
  providedIn: 'root'
})
export class AdnAuthHelperService {
  constructor(
    private readonly authenticatedUserResource: AuthenticatedUserService,
    @Inject(WINDOW) private readonly window: any,
    private readonly toastr: ToastrService,
    private readonly electionApi: ElectionApiService,
    private readonly LocalUserPermissions: LocalUserPermissions,
    private readonly eventBus: AppEventBusService,
    private readonly WorkspaceRegistry: WorkspaceRegistryService,
    private readonly LocalNetworkProfile: LocalNetworkProfile,
    @Inject(NetworkInfoApiService) private readonly NetworkInfo: NetworkInfoApiService,
    private readonly localUserProfile: LocalUserProfile,
    private readonly apiConfig: ApiConfig,
    private readonly localPausing: LocalPausing,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
  }

  doAuth(ctrlProfile: any, credentials: any, masqueradeAttempt?: any, networkTag?: any): any {
    UpdateSession.obtain().destroy();
    return this.authenticatedUserResource.get().then((authUser: any) => {
      const user = authUser.user;
      const prevDefaultNetworkTagId = networkTag
        || this.LocalNetworkProfile.get('tagId')
        || this.localUserProfile.get('defaultNetworkTagId');
      const prevApp = this.localUserProfile.get('app');
      const isPrevUser = this.localUserProfile.isSameUser(ctrlProfile);
      const profile = this.localUserProfile.create(
        user,
        _.keys(authUser.appsByNetwork),
        authUser.profile,
        credentials.masqUsername && masqueradeAttempt ? credentials.username : null
      );

      this.LocalUserPermissions.create(authUser);
      this.localPausing.destroy();

      if (isPrevUser && prevDefaultNetworkTagId && this.localUserProfile.hasNetwork(prevDefaultNetworkTagId)) {
        profile.defaultNetworkTagId = prevDefaultNetworkTagId;
        this.localUserProfile.save();
      } else {
        const networkWithMostTeams = this.LocalUserPermissions.getNetworkWithMostTeams(profile.networkTagIds);
        if (networkWithMostTeams) {
          profile.defaultNetworkTagId = networkWithMostTeams;
          this.localUserProfile.save();
        }
      }

      if (isPrevUser) {
        this.localUserProfile.updateDefaultNetworkId(this.LocalNetworkProfile.get('tagId'));
      }

      // create() installs the selected tag synchronously before starting its own
      // network/profile requests, so the remaining context-scoped bootstrap calls
      // can share the same request wave instead of adding another round trip.
      const bootstrapRefresh = Promise.all([
        this.LocalNetworkProfile.create(profile),
        firstValueFrom(this.NetworkInfo.get()).then((data: any) => {
          this.localNetworkInfo.setInfo(data);
        }),
        firstValueFrom(this.electionApi.list({includeTests: true})).then((data: any) => {
          this.localNetworkInfo.setSuppInfo('elections', data.results);
        })
      ]);

      const availableApps = this.LocalUserPermissions.getApps();
      if (isPrevUser && prevApp && _.includes(availableApps, prevApp)) {
        profile.app = prevApp;
        this.localUserProfile.save();
      } else if (availableApps.length > 1 && _.includes(availableApps, 'ADVERTISING')) {
        profile.app = 'ADVERTISING';
        this.localUserProfile.save();
      }

      this.WorkspaceRegistry.sync();

      LocalDebugInfo.obtain().setSignIn(
        user.id,
        user.username,
        credentials.username,
        masqueradeAttempt ? credentials.masqUsername : null
      );

      return bootstrapRefresh.then(() => {
        if (this.apiConfig.getEnv() === 'production') {
          const roles = _.sortBy(_.uniq(_.map(this.LocalUserPermissions.getAllVisPermissions(), (_v: any, k: any) => {
            return k;
          })));
          this.window.pendo.identify({
            visitor: {
              id: this.localUserProfile.get('userId'),
              email: this.localUserProfile.get('username'),
              full_name: this.localUserProfile.get('surname')
                ? this.localUserProfile.get('name') + this.localUserProfile.get('surname')
                : this.localUserProfile.get('displayName'),
              role: roles,
              role_string: roles.join(','),
              is_admin: roles.indexOf('MANAGE_SYSTEM') > -1
            },
            account: {
              id: this.LocalNetworkProfile.get('tagId'),
              name: this.LocalNetworkProfile.get('networkName'),
              teams: this.LocalUserPermissions.get('allNetworks').length > 3
                ? 'ADMIN'
                : _.uniq(this.LocalUserPermissions.get('allTeams')).join(',')
            }
          });
        }

        this.eventBus.emit(AUTH_EVENT.loginSuccess, profile);

        // do not show a password expiry notification when masquerading its just confusing
        if (!this.localUserProfile.isMasquerading() && profile.passwordExpiresDays && profile.passwordExpiresDays < 30) {
          this.toastr.warning(
            `Your password expires in ${profile.passwordExpiresDays} day${profile.passwordExpiresDays === 1 ? '' : 's'}.<br /><a href="/admin/user-profile" class="toast-link">Change Your Password</a>`,
            null,
            {enableHtml: true, timeOut: 10000}
          );
        }
      });
    });
  }
}
